import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MagnifyingGlassIcon, PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { clientService } from '../../services/clientService';
import { Client, ClientStatus, getFullName, getClientStatusColor } from '../../types/client';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DeleteConfirmModal from '../../components/UI/DeleteConfirmModal';
import ClientModal from '../../components/Clients/ClientModal';
import { usePermission } from '../../hooks/usePermission';

import { routeDeliveryService } from '../../services/routeDeliveryService';
import { dietService } from '../../services/dietService';
import { Dialog, Transition } from '@headlessui/react';
import DataGrid from '../../components/DataGrid';
import { ColumnDef } from '@tanstack/react-table';



const ClientsPage: React.FC = () => {
  const { canCreate, canEdit, canDelete } = usePermission();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<ClientStatus | ''>('');
  const [filters, setFilters] = useState<{ name?: string }>({});
  const [sort, setSort] = useState<{ column: string; order: 'asc' | 'desc' }>({ column: 'name', order: 'asc' });


  const pageSize = 50;

  const [actionsFor, setActionsFor] = useState<Client | null>(null);
  const [selectedRouteId, setSelectedRouteId] = useState<string>('');
  const [selectedDietId, setSelectedDietId] = useState<string>('');
  const [selectedPriceId, setSelectedPriceId] = useState<string>('');

  const [showAddDelivery, setShowAddDelivery] = useState(false);
  const [showRearrange, setShowRearrange] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<Client | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Client | null>(null);
  const queryClient = useQueryClient();


  // Fetch clients
  const { column: sortBy, order: sortOrder } = sort;

  const { data, isLoading, error } = useQuery({
    queryKey: ['clients', currentPage, searchTerm, statusFilter, filters, sortBy, sortOrder],
    queryFn: () => clientService.getClients({
      page: currentPage,
      limit: pageSize,
      search: searchTerm,
      status: statusFilter || undefined,
      sortBy,
      sortOrder,
      name: filters.name,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => clientService.deleteClient(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['clients'] }); setConfirmDelete(null); },
  });

  const openCreate = useCallback(() => { setSelected(null); setIsModalOpen(true); }, []);
  const openEdit = useCallback((c: Client) => { setSelected(c); setIsModalOpen(true); }, []);













  const confirmMutation = useMutation({
    mutationFn: (id: string) => clientService.confirm(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['clients'] }); setActionsFor(null); },
  });
  const freezeMutation = useMutation({
    mutationFn: (id: string) => clientService.freeze(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['clients'] }); setActionsFor(null); },
  });
  const unfreezeMutation = useMutation({
    mutationFn: (id: string) => clientService.unfreeze(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['clients'] }); setActionsFor(null); },
  });
  const stopDeliveryMutation = useMutation({
    mutationFn: (id: string) => clientService.stopDelivery(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['clients'] }); setActionsFor(null); },
  });
  const resumeDeliveryMutation = useMutation({
    mutationFn: (id: string) => clientService.resumeDelivery(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['clients'] }); setActionsFor(null); },
  });

  // Modals data fetch (lazy)
  const { data: routesResp } = useQuery({
    queryKey: ['route-deliveries', 'all'],
    queryFn: () => routeDeliveryService.getRouteDeliveries({ page: 1, limit: 1000 }),
    enabled: showAddDelivery,
  });
  const routes = routesResp?.routeDeliveries || [];

  const { data: dietsResp } = useQuery({
    queryKey: ['diets', 'all'],
    queryFn: () => dietService.getDiets({ page: 1, limit: 1000 }),
    enabled: showRearrange,
  });
  const { data: pricesResp } = useQuery({
    queryKey: ['prices', 'all'],
    queryFn: () => dietService.getPrices({ page: 1, limit: 1000 }),
    enabled: showRearrange,
  });
  const diets = dietsResp?.diets || [];
  const prices = pricesResp?.prices || [];

  const addDeliveryMutation = useMutation({
    mutationFn: (p: { id: string; routeDeliveryId?: string }) => clientService.addDelivery(p.id, p.routeDeliveryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setShowAddDelivery(false);
      setSelectedRouteId('');
      setActionsFor(null);
    },
  });

  const rearrangeMutation = useMutation({
    mutationFn: (p: { id: string; dietId?: string; priceId?: string }) => clientService.rearrangeMealSize(p.id, { dietId: p.dietId, priceId: p.priceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setShowRearrange(false);
      setSelectedDietId('');
      setSelectedPriceId('');
      setActionsFor(null);
    },
  });




  const handleExport = useCallback(async () => {
    try {
      const blob = await clientService.exportClients({
        status: statusFilter || undefined,
        search: searchTerm,
        ids: selectedIds.length ? selectedIds : undefined,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `clients-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  }, [searchTerm, selectedIds, statusFilter]);

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const handleBulkAction = useCallback(async (status: ClientStatus) => {
    if (!selectedIds.length) return;
    try {
      await clientService.bulkUpdateClientStatus(selectedIds, status);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    } catch (error) {
      console.error('Bulk update failed:', error);
    }
  }, [queryClient, selectedIds]);


  const handleOpenActions = useCallback((client: Client) => {
    setActionsFor(client);
  }, []);

  const handleDeleteClient = useCallback((client: Client) => {
    setConfirmDelete(client);
  }, []);

  const clients = data?.clients || [];
  const pagination = data?.pagination;

  const columns = useMemo<ColumnDef<Client>[]>(() => [
    {
      id: 'firstName',
      accessorKey: 'firstName',
      header: 'First Name',
      meta: { sortable: true },
      cell: ({ row }) => row.original.firstName || '-',
    },
    {
      id: 'fatherName',
      accessorKey: 'fatherName',
      header: 'Father Name',
      cell: ({ row }) => (row.original as any).fatherName || '-',
    },
    {
      id: 'lastName',
      accessorKey: 'lastName',
      header: 'Last Name',
      meta: { sortable: true },
      cell: ({ row }) => row.original.lastName || '-',
    },
    {
      id: 'nationality',
      header: 'Nationality',
      cell: ({ row }) => (row.original as any).nationality?.name || '-',
    },
    {
      id: 'location',
      header: 'Location',
      cell: ({ row }) => (row.original as any).location?.name || row.original.city?.name || '-',
    },
    {
      id: 'phone',
      accessorKey: 'phone',
      header: 'Mobile Phone',
      cell: ({ row }) => row.original.phone || (row.original as any).mobilePhone || '-',
    },
    {
      id: 'diet',
      header: 'Diet',
      cell: ({ row }) => (row.original as any).diet?.name || '-',
    },
    {
      id: 'delivery',
      header: 'Delivery',
      cell: ({ row }) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(row.original as any).delivery ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {(row.original as any).delivery ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      meta: { sortable: true },
      cell: ({ row }) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getClientStatusColor(row.original.status)}`}>
          {row.original.status}
        </span>
      ),
    },
    {
      id: 'confirmationDate',
      header: 'Confirmation Date',
      cell: ({ row }) => (row.original as any).confirmationDate ? new Date((row.original as any).confirmationDate).toLocaleDateString() : '-',
    },
    {
      id: 'statusDate',
      header: 'Status Date',
      cell: ({ row }) => row.original.statusDate ? new Date(row.original.statusDate).toLocaleDateString() : '-',
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-end space-x-2">
          <Button variant="secondary" size="sm" onClick={() => handleOpenActions(row.original)}>Actions</Button>
          {canEdit('CLIENTS') && (
            <button onClick={() => openEdit(row.original)} className="text-primary-600 hover:text-primary-800">
              <PencilSquareIcon className="w-4 h-4" />
            </button>
          )}
          {canDelete('CLIENTS') && (
            <button onClick={() => handleDeleteClient(row.original)} className="text-red-600 hover:text-red-800">
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ], [handleDeleteClient, handleOpenActions, openEdit, canEdit, canDelete]);

  const handleSortChange = (columnId: string) => {
    setSort((prev) => {
      if (prev.column === columnId) {
        return { column: columnId, order: prev.order === 'asc' ? 'desc' : 'asc' };
      }
      return { column: columnId, order: 'asc' };
    });
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Error loading clients. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={handleExport}>Export to Excel</Button>
          {canCreate('CLIENTS') && (
            <Button onClick={openCreate}><PlusIcon className="w-5 h-5 mr-2" />Create</Button>
          )}
        </div>
      </div>
      {selectedIds.length > 0 && (
        <div className="bg-white rounded-lg shadow-soft p-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">{selectedIds.length} selected</div>
          <div className="flex space-x-2">
            <Button onClick={() => handleBulkAction('ACTIVE')}>Activate</Button>
            <Button variant="danger" onClick={() => handleBulkAction('INACTIVE')}>Deactivate</Button>
            <Button variant="secondary" onClick={() => handleBulkAction('FROZEN')}>Freeze</Button>
            <Button variant="secondary" onClick={() => handleBulkAction('SUSPENDED')}>Suspend</Button>
            <Button variant="secondary" onClick={() => handleBulkAction('STOPPED')}>Stop</Button>
            <Button variant="outline" onClick={() => handleBulkAction('PENDING')}>Mark Pending</Button>
            <Button variant="secondary" onClick={() => setSelectedIds([])}>Clear selection</Button>
          </div>
        </div>
      )}


      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients..."
              className="pl-10 form-input"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <div className="flex items-center space-x-4">
            <div className="w-48">
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                className="form-input"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as ClientStatus | '');
                  setCurrentPage(1);
                }}
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="ACTIVE">Active</option>
                <option value="FROZEN">Frozen</option>
                <option value="STOPPED">Stopped</option>
                <option value="INACTIVE">Inactive</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
            </div>
            <div className="w-48">
              <label className="block text-xs text-gray-500 mb-1">Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter name..."
                value={filters.name ?? ''}
                onChange={(e) => {
                  const value = e.target.value || undefined;
                  setFilters({ name: value });
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><LoadingSpinner size="lg" /></div>
          ) : (
            <DataGrid
              data={clients}
              columns={columns}
              getRowId={(row) => row.id}
              enableSelection
              selectedIds={selectedIds}
              onToggleRow={(id) => toggleRow(id)}
              onToggleAll={(ids, checked) =>
                setSelectedIds((prev) =>
                  checked ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id))
                )
              }
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
          )}
        </div>

        {pagination && (
          <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to <span className="font-medium">{Math.min(currentPage * pageSize, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results
            </p>
            <div className="space-x-2">
              <Button variant="secondary" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Previous</Button>
              <Button variant="secondary" disabled={pagination && currentPage === pagination.pages} onClick={() => setCurrentPage((p) => (pagination ? Math.min(pagination.pages, p + 1) : p + 1))}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <ClientModal
          client={selected}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => { setIsModalOpen(false); queryClient.invalidateQueries({ queryKey: ['clients'] }); }}
        />
      )}

      {confirmDelete && (
        <DeleteConfirmModal
          title="Delete Client"
          message={`Are you sure you want to delete "${getFullName(confirmDelete)}"? This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteMutation.mutate(confirmDelete.id)}
          loading={deleteMutation.isPending}
        />
      )}

      {actionsFor && (
        <Transition appear show={true} as={React.Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => { if (!confirmMutation.isPending && !freezeMutation.isPending && !unfreezeMutation.isPending && !stopDeliveryMutation.isPending && !resumeDeliveryMutation.isPending && !addDeliveryMutation.isPending && !rearrangeMutation.isPending) { setActionsFor(null); setShowAddDelivery(false); setShowRearrange(false); } }}>
            <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">

                    {!showAddDelivery && !showRearrange && (
                      <>
                        <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900 mb-4">Actions for {getFullName(actionsFor)}</Dialog.Title>
                        <div className="grid grid-cols-2 gap-3">
                          <Button onClick={() => confirmMutation.mutate(actionsFor.id)} loading={confirmMutation.isPending}>Confirm</Button>
                          {actionsFor.status !== 'FROZEN' ? (
                            <Button variant="secondary" onClick={() => freezeMutation.mutate(actionsFor.id)} loading={freezeMutation.isPending}>Freeze</Button>
                          ) : (
                            <Button variant="secondary" onClick={() => unfreezeMutation.mutate(actionsFor.id)} loading={unfreezeMutation.isPending}>Unfreeze</Button>
                          )}
                          <Button variant="danger" onClick={() => stopDeliveryMutation.mutate(actionsFor.id)} loading={stopDeliveryMutation.isPending}>Stop Delivery</Button>
                          <Button variant="success" onClick={() => resumeDeliveryMutation.mutate(actionsFor.id)} loading={resumeDeliveryMutation.isPending}>Resume Delivery</Button>
                          <Button variant="outline" onClick={() => { setShowAddDelivery(true); }}>Add Delivery (Route)</Button>
                          <Button variant="outline" onClick={() => { setShowRearrange(true); }}>Rearrange Meal Size</Button>
                        </div>
                        <div className="mt-6 text-right">
                          <Button variant="secondary" onClick={() => setActionsFor(null)} disabled={confirmMutation.isPending || freezeMutation.isPending || unfreezeMutation.isPending || stopDeliveryMutation.isPending || resumeDeliveryMutation.isPending}>Close</Button>
                        </div>
                      </>
                    )}

                    {showAddDelivery && (
                      <>
                        <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900 mb-4">Add Delivery for {getFullName(actionsFor)}</Dialog.Title>
                        <div className="space-y-3">
                          <label className="block text-sm font-medium text-gray-700">Route Delivery</label>
                          <select className="form-input w-full" value={selectedRouteId} onChange={(e) => setSelectedRouteId(e.target.value)}>
                            <option value="">No change</option>
                            {routes.map(r => (
                              <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="mt-6 flex justify-end space-x-3">
                          <Button variant="secondary" onClick={() => { setShowAddDelivery(false); setSelectedRouteId(''); }}>Back</Button>
                          <Button onClick={() => addDeliveryMutation.mutate({ id: actionsFor.id, routeDeliveryId: selectedRouteId || undefined })} loading={addDeliveryMutation.isPending}>Save</Button>
                        </div>
                      </>
                    )}

                    {showRearrange && (
                      <>
                        <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900 mb-4">Rearrange Meal Size for {getFullName(actionsFor)}</Dialog.Title>
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Diet</label>
                            <select className="form-input w-full" value={selectedDietId} onChange={(e) => setSelectedDietId(e.target.value)}>
                              <option value="">No change</option>
                              {diets.map(d => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Price</label>
                            <select className="form-input w-full" value={selectedPriceId} onChange={(e) => setSelectedPriceId(e.target.value)}>
                              <option value="">No change</option>
                              {prices.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="mt-6 flex justify-end space-x-3">
                          <Button variant="secondary" onClick={() => { setShowRearrange(false); setSelectedDietId(''); setSelectedPriceId(''); }}>Back</Button>
                          <Button onClick={() => rearrangeMutation.mutate({ id: actionsFor.id, dietId: selectedDietId || undefined, priceId: selectedPriceId || undefined })} loading={rearrangeMutation.isPending} disabled={!selectedDietId && !selectedPriceId}>Save</Button>
                        </div>
                      </>
                    )}

                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}


    </div>
  );
};

export default ClientsPage;
